/**
 * Cloud artifact upload — binary file upload via presigned S3 URLs.
 *
 * Flow per artifact:
 *   1. POST /artifacts/upload-url → receive presigned PUT URL + artifactId
 *   2. PUT binary file to the presigned URL
 *   3. POST /artifacts/confirm → mark upload complete
 *
 * Never throws to caller — returns structured results.
 */

import { createReadStream, statSync } from 'node:fs';
import { basename } from 'node:path';
import { Readable } from 'node:stream';
import type { ReadableStream as WebReadableStream } from 'node:stream/web';
import { Budget, putDeadlineMs, withTimeout } from './cloud-deadline.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ArtifactUploadRequest {
  readonly filePath: string;
  readonly runId: string;
  readonly testId: string;
  readonly type: 'screenshot' | 'video' | 'trace' | 'other';
}

export interface ArtifactUploadResult {
  readonly success: boolean;
  readonly storageKey: string | null;
  readonly artifactId: string | null;
  readonly error: string | null;
}

interface UploadUrlResponse {
  readonly artifactId: string;
  readonly uploadUrl: string;
  readonly storageKey: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const UPLOAD_CONCURRENCY = 5;
const RETRY_DELAYS_MS = [1000, 3000, 9000];
const MAX_RETRIES = 3;
/** The small JSON calls around a transfer (presign, confirm). The transfer
 *  itself gets a size-scaled deadline — see cloud-deadline. */
const JSON_REQUEST_TIMEOUT_MS = 30_000;

const CONTENT_TYPE_MAP: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.webm': 'video/webm',
  '.mp4': 'video/mp4',
  '.zip': 'application/zip',
};

// ---------------------------------------------------------------------------
// Concurrency Limiter
// ---------------------------------------------------------------------------

let activeUploads = 0;
const pendingUploads: Array<() => void> = [];

async function acquireSlot(): Promise<void> {
  if (activeUploads >= UPLOAD_CONCURRENCY) {
    await new Promise<void>((resolve) => pendingUploads.push(resolve));
  }
  activeUploads++;
}

function releaseSlot(): void {
  activeUploads--;
  if (pendingUploads.length > 0) pendingUploads.shift()!();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getContentType(filePath: string): string {
  const ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
  return CONTENT_TYPE_MAP[ext] ?? 'application/octet-stream';
}

function getFileSize(filePath: string): number {
  try {
    return statSync(filePath).size;
  } catch {
    return 0;
  }
}

// ---------------------------------------------------------------------------
// Core Upload
// ---------------------------------------------------------------------------

async function requestUploadUrl(
  endpoint: string,
  accessToken: string,
  request: ArtifactUploadRequest,
  sizeBytes: number,
  contentType: string,
): Promise<UploadUrlResponse | null> {
  const url = `${endpoint}/artifacts/upload-url`;
  const body = JSON.stringify({
    runId: request.runId,
    testId: request.testId,
    fileName: basename(request.filePath),
    contentType,
    type: request.type,
    sizeBytes,
  });

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await withTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body,
      }, JSON_REQUEST_TIMEOUT_MS);

      if (response.ok) {
        return (await response.json()) as UploadUrlResponse;
      }

      if (response.status >= 500 && attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }

      return null;
    } catch {
      if (attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      return null;
    }
  }
  return null;
}

async function putFileToPresignedUrl(
  presignedUrl: string,
  filePath: string,
  contentType: string,
  sizeBytes: number,
): Promise<boolean> {
  const deadlineMs = putDeadlineMs(sizeBytes, JSON_REQUEST_TIMEOUT_MS);
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const nodeStream = createReadStream(filePath);
      const webStream = Readable.toWeb(nodeStream) as WebReadableStream<Uint8Array>;

      const response = await withTimeout(
        presignedUrl,
        {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
            'Content-Length': String(sizeBytes),
          },
          body: webStream,
          // Node's fetch requires `duplex` for a stream body; it is not in the
          // DOM RequestInit type, hence the cast rather than a suppression.
          ...({ duplex: 'half' } as Record<string, unknown>),
        } as RequestInit,
        // A transfer's budget follows its size — a 200 MB trace legitimately
        // outlasts a 2 KB POST, and a flat cap would abandon healthy uploads
        // on a slow link while still leaving a wedged socket to undici.
        deadlineMs,
      );

      if (response.ok) return true;

      if (response.status >= 500 && attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }

      return false;
    } catch {
      if (attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      return false;
    }
  }
  return false;
}

async function confirmUpload(
  endpoint: string,
  accessToken: string,
  artifactId: string,
): Promise<boolean> {
  const url = `${endpoint}/artifacts/confirm`;
  try {
    const response = await withTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ artifactId }),
    }, JSON_REQUEST_TIMEOUT_MS);
    return response.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Upload a single artifact file to cloud storage via presigned URL.
 * Concurrency-limited and retried. Never throws.
 */
export async function uploadArtifact(
  endpoint: string,
  accessToken: string,
  request: ArtifactUploadRequest,
  maxSizeMb: number,
): Promise<ArtifactUploadResult> {
  const sizeBytes = getFileSize(request.filePath);

  if (sizeBytes === 0) {
    return { success: false, storageKey: null, artifactId: null, error: 'file_not_found_or_empty' };
  }

  if (sizeBytes > maxSizeMb * 1024 * 1024) {
    return { success: false, storageKey: null, artifactId: null, error: 'file_too_large' };
  }

  const contentType = getContentType(request.filePath);

  await acquireSlot();
  try {
    const urlResult = await requestUploadUrl(
      endpoint,
      accessToken,
      request,
      sizeBytes,
      contentType,
    );

    if (!urlResult) {
      return { success: false, storageKey: null, artifactId: null, error: 'upload_url_request_failed' };
    }

    const uploaded = await putFileToPresignedUrl(
      urlResult.uploadUrl,
      request.filePath,
      contentType,
      sizeBytes,
    );

    if (!uploaded) {
      return {
        success: false,
        storageKey: urlResult.storageKey,
        artifactId: urlResult.artifactId,
        error: 'presigned_put_failed',
      };
    }

    const confirmed = await confirmUpload(endpoint, accessToken, urlResult.artifactId);

    return {
      success: confirmed,
      storageKey: urlResult.storageKey,
      artifactId: urlResult.artifactId,
      error: confirmed ? null : 'confirm_failed',
    };
  } finally {
    releaseSlot();
  }
}

/**
 * Upload multiple artifacts in parallel (bounded by UPLOAD_CONCURRENCY).
 * Returns a map from filePath to the upload result.
 */
export async function uploadArtifacts(
  endpoint: string,
  accessToken: string,
  requests: readonly ArtifactUploadRequest[],
  maxSizeMb: number,
  budget?: Budget,
): Promise<Map<string, ArtifactUploadResult>> {
  const results = new Map<string, ArtifactUploadResult>();

  const promises = requests.map(async (req) => {
    // ⛔ The circuit breaker. Without it, a run whose storage endpoint is
    // wedged pays one full deadline PER ARTIFACT — ten screenshots turned a
    // slow upload into an app that would not exit. Once the phase's budget is
    // gone, the rest are recorded as skipped and the process gets to leave;
    // the local report still holds everything, and the next run re-uploads.
    if (budget?.expired()) {
      results.set(req.filePath, {
        success: false,
        storageKey: null,
        artifactId: null,
        error: 'upload_budget_exhausted',
      });
      return;
    }
    const result = await uploadArtifact(endpoint, accessToken, req, maxSizeMb);
    results.set(req.filePath, result);
  });

  await Promise.allSettled(promises);
  return results;
}
